import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Sidebar from '../components/Sidebar';
import ImageUploader from '../components/ImageUploader';
import { useConfirm } from '../components/ConfirmDialog';
import Tooltip from '../components/Tooltip';
import { ImportModal } from '../components/ImportModal';
import { useAuth } from '../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, Cell } from 'recharts';

// ── Create Org Modal ──────────────────────────────────────────────────────────
function CreateOrgModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name:'', slug:'', description:'', contact_email:'', org_admin_email:'', org_admin_name:'', org_admin_password:'' });
  const [loading, setLoading] = useState(false);
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const autoSlug = n => n.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post('/api/organizations', form);
      toast.success(`Organization "${res.data.name}" created!`);
      onCreated(); onClose();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:560 }}>
        <h3 className="modal-title">🏢 Create New Organization</h3>
        <form onSubmit={submit}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <div className="form-group">
              <label className="form-label">Organization Name *</label>
              <input className="form-input" value={form.name} required
                onChange={e => setForm(p => ({...p, name:e.target.value, slug:autoSlug(e.target.value)}))}
                placeholder="Tech Institute of Excellence" />
            </div>
            <div className="form-group">
              <label className="form-label">Slug (URL identifier) *</label>
              <input className="form-input" value={form.slug} required onChange={f('slug')} placeholder="tech-institute" />
            </div>
            <div className="form-group" style={{ gridColumn:'1/-1' }}>
              <label className="form-label">Description</label>
              <input className="form-input" value={form.description} onChange={f('description')} placeholder="Brief description" />
            </div>
            <div className="form-group">
              <label className="form-label">Contact Email</label>
              <input className="form-input" type="email" value={form.contact_email} onChange={f('contact_email')} />
            </div>
          </div>
          <div style={{ borderTop:'1px solid var(--border)', paddingTop:14, marginTop:8 }}>
            <div style={{ fontSize:13, fontWeight:600, color:'var(--text-secondary)', marginBottom:10 }}>Organization Admin Login (optional)</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div className="form-group">
                <label className="form-label">Admin Full Name</label>
                <input className="form-input" value={form.org_admin_name} onChange={f('org_admin_name')} placeholder="John Smith" />
              </div>
              <div className="form-group">
                <label className="form-label">Admin Email</label>
                <input className="form-input" type="email" value={form.org_admin_email} onChange={f('org_admin_email')} />
              </div>
              <div className="form-group">
                <label className="form-label">Admin Password</label>
                <input className="form-input" type="password" value={form.org_admin_password} onChange={f('org_admin_password')} placeholder="Min 6 chars" />
              </div>
            </div>
          </div>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:18 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Creating...' : '✓ Create Organization'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Edit Org Modal ────────────────────────────────────────────────────────────
function EditOrgModal({ org, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:org.name, description:org.description||'', contact_email:org.contact_email||'',
    logo_url:org.logo_url||'',
    org_admin_email:'', org_admin_new_password:'',
  });
  const [loading, setLoading]   = useState(false);
  const [orgAdmin, setOrgAdmin] = useState(null);
  const [showPwd,  setShowPwd]  = useState(false);
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  // Load org admin details
  React.useEffect(() => {
    axios.get(`/api/organizations/${org.id}/members?role=organization`)
      .then(r => { if (r.data[0]) setOrgAdmin(r.data[0]); })
      .catch(() => {});
  }, [org.id]);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await axios.put(`/api/organizations/${org.id}`, { ...form, is_active:org.is_active });
      if (orgAdmin && form.org_admin_email) {
        await axios.patch(`/api/organizations/${org.id}/members/${orgAdmin.id}/credentials`, {
          email: form.org_admin_email,
        });
      }
      if (form.org_admin_new_password) {
        const pwdRes = await axios.post(`/api/organizations/${org.id}/admin-credentials/reset`, {
          new_password: form.org_admin_new_password,
        });
        toast.success(`✅ Updated! Org admin password: ${pwdRes.data.plain_password}`, { autoClose: 12000 });
      } else {
        toast.success('Organization updated');
      }
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setLoading(false); }
  };
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:520, maxHeight:'85vh', overflowY:'auto' }}>
        <h3 className="modal-title">✏️ Edit — {org.name}</h3>
        <form onSubmit={submit}>
          <div className="form-group"><label className="form-label">Organization Name *</label><input className="form-input" value={form.name} onChange={f('name')} required /></div>
          <div className="form-group"><label className="form-label">Description</label><textarea className="form-input form-textarea" style={{ minHeight:70 }} value={form.description} onChange={f('description')} /></div>
          <div className="form-group"><label className="form-label">Contact Email</label><input className="form-input" type="email" value={form.contact_email} onChange={f('contact_email')} /></div>
          <div className="form-group">
            <label className="form-label">Organization Logo</label>
            <div style={{ display:'flex', gap:16, alignItems:'flex-start' }}>
              <ImageUploader
                currentUrl={form.logo_url}
                shape="rect"
                size={80}
                label="Upload Logo"
                hint="JPG, PNG or WebP · max 2MB"
                onUpload={async (base64) => {
                  // Store base64 directly — saved to DB when form submits
                  setForm(p => ({ ...p, logo_url: base64 }));
                }}
              />
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:6 }}>
                  Click the box to upload, or paste a URL below.
                </div>
                <input className="form-input" value={form.logo_url?.startsWith('data:') ? '' : (form.logo_url||'')}
                  onChange={f('logo_url')} placeholder="https://example.com/logo.png" />
                {form.logo_url && !form.logo_url.startsWith('data:') && (
                  <img src={form.logo_url} alt="preview" style={{ height:36, marginTop:6, borderRadius:4, objectFit:'contain', border:'1px solid var(--border)', background:'#fff', padding:2 }} onError={e => e.target.style.display='none'} />
                )}
              </div>
            </div>
          </div>

          {/* Org Admin credentials */}
          <div style={{ borderTop:'1px solid var(--border)', paddingTop:14, marginTop:8 }}>
            <div style={{ fontSize:13, fontWeight:600, color:'var(--text-secondary)', marginBottom:12 }}>
              Organization Admin Account
              {orgAdmin && <span style={{ fontSize:11, color:'var(--text-muted)', marginLeft:8 }}>Current: {orgAdmin.email}</span>}
            </div>
            <div className="form-group">
              <label className="form-label">Change Admin Email</label>
              <input className="form-input" type="email" value={form.org_admin_email}
                onChange={f('org_admin_email')} placeholder={orgAdmin?.email || 'No org admin yet'} />
            </div>
            <div className="form-group">
              <label className="form-label">Set New Password <span style={{ fontSize:11, color:'var(--text-muted)' }}>(will be shown in plain text once)</span></label>
              <div style={{ display:'flex', gap:8 }}>
                <div style={{ flex:1, position:'relative' }}>
                  <input className="form-input" type={showPwd ? 'text' : 'password'}
                    value={form.org_admin_new_password} onChange={f('org_admin_new_password')}
                    placeholder="Enter new password to set" />
                  <button type="button" onClick={() => setShowPwd(p => !p)}
                    style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:14 }}>
                    {showPwd ? '🙈' : '👁'}
                  </button>
                </div>
              </div>
              <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>
                ⚠ Passwords are hashed and cannot be recovered. Set a new one to share it with the org admin.
              </div>
            </div>
          </div>

          <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:18 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : '✓ Save Changes'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Block Org Modal ───────────────────────────────────────────────────────────
function BlockOrgModal({ org, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const isBlocking = org.is_active;
  const submit = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`/api/organizations/${org.id}/${isBlocking?'block':'unblock'}`, { reason });
      toast.success(res.data.message); onDone(); onClose();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setLoading(false); }
  };
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:460 }}>
        <h3 className="modal-title" style={{ color:isBlocking?'var(--accent-danger)':'var(--accent-success)' }}>
          {isBlocking ? '⛔ Block Organization' : '✅ Unblock Organization'}
        </h3>
        <div className="alert" style={{ marginBottom:16, background:isBlocking?'rgba(239,68,68,0.1)':'rgba(16,185,129,0.1)', borderColor:isBlocking?'var(--accent-danger)':'var(--accent-success)' }}>
          <strong>{org.name}</strong> — {isBlocking
            ? 'All students, instructors, and org admin will immediately lose access. Active exam sessions will be interrupted.'
            : 'Organization and all its users will regain full platform access.'}
        </div>
        {isBlocking && (
          <div className="form-group">
            <label className="form-label">Reason for blocking</label>
            <textarea className="form-input form-textarea" style={{ minHeight:80 }} value={reason} onChange={e => setReason(e.target.value)} placeholder="Policy violation, payment issue..." />
          </div>
        )}
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:16 }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className={`btn ${isBlocking?'btn-danger':'btn-success'}`} onClick={submit} disabled={loading}>
            {loading ? 'Processing...' : isBlocking ? '⛔ Block' : '✅ Unblock'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Resume / Time Adjust Modals ───────────────────────────────────────────────
function ResumeModal({ attempt, onClose, onConfirm }) {
  const [extra,       setExtra]       = useState(0);
  const [useFullTime, setUseFullTime] = useState(false);
  const [loading,     setLoading]     = useState(false);
  const remainingMins = attempt.remaining_seconds ? Math.ceil(attempt.remaining_seconds/60) : 0;
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:480 }}>
        <h3 className="modal-title">▶ Resume Exam</h3>
        <div style={{ fontSize:14, color:'var(--text-secondary)', marginBottom:14 }}>
          <div><strong>Student:</strong> {attempt.student_name}</div>
          <div><strong>Exam:</strong> {attempt.exam_title}</div>
          <div style={{ marginTop:4 }}><strong>Remaining when terminated:</strong>{' '}
            <span style={{ fontFamily:'var(--font-mono)', color:'var(--accent-warning)' }}>{remainingMins} min</span>
          </div>
        </div>
        {/* Time mode choice */}
        <div style={{ marginBottom:16 }}>
          <label className="form-label">Resume with</label>
          <div style={{ display:'flex', gap:10, marginTop:8 }}>
            {[
              { val:false, icon:'⏱', label:'Remaining Time', sub:`${remainingMins} min`, color:'var(--accent-primary)' },
              { val:true,  icon:'⏰', label:'Full Duration',   sub:'Fresh start',         color:'var(--accent-success)' },
            ].map(opt => (
              <button key={opt.label} onClick={() => setUseFullTime(opt.val)}
                style={{ flex:1, padding:'10px 14px', borderRadius:8, border:'2px solid', cursor:'pointer', textAlign:'center', fontSize:13,
                  borderColor: useFullTime===opt.val ? opt.color : 'var(--border)',
                  background:  useFullTime===opt.val ? `${opt.color}18` : 'transparent',
                  color:       useFullTime===opt.val ? opt.color : 'var(--text-secondary)',
                  fontWeight:  useFullTime===opt.val ? 700 : 400 }}>
                <div style={{ fontSize:22, marginBottom:4 }}>{opt.icon}</div>
                <div>{opt.label}</div>
                <div style={{ fontSize:11, marginTop:2, fontFamily:'var(--font-mono)' }}>{opt.sub}</div>
              </button>
            ))}
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">+ Extra bonus minutes (optional)</label>
          <input className="form-input" type="number" min="0" max="120" value={extra} onChange={e => setExtra(parseInt(e.target.value)||0)} style={{ width:100 }} />
        </div>
        <div className="alert alert-info" style={{ fontSize:12, marginTop:8 }}>Previous answers will be restored automatically.</div>
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:16 }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-success" disabled={loading} onClick={async () => {
            setLoading(true);
            try { await onConfirm(attempt.id, extra, useFullTime); onClose(); } finally { setLoading(false); }
          }}>
            {loading ? 'Resuming...' : `▶ Resume (${useFullTime?'Full':remainingMins+'min'}${extra?` +${extra}`:''}) `}
          </button>
        </div>
      </div>
    </div>
  );
}

function TimeAdjustModal({ attempt, onClose, onConfirm }) {
  const [minutes, setMinutes] = useState(5);
  const [action, setAction] = useState('add');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:420 }}>
        <h3 className="modal-title">⏱ Adjust Time — {attempt.student_name}</h3>
        <div style={{ display:'flex', gap:8, marginBottom:14 }}>
          {['add','deduct'].map(a => (
            <button key={a} className="btn btn-secondary btn-sm" onClick={() => setAction(a)}
              style={action===a ? { borderColor:'var(--accent-primary)', color:'var(--accent-primary)', background:'rgba(79,142,247,0.1)' } : {}}>
              {a==='add' ? '+ Add Time' : '− Deduct Time'}
            </button>
          ))}
        </div>
        <div className="form-group">
          <label className="form-label">Minutes</label>
          <input className="form-input" type="number" min="1" max="120" value={minutes} onChange={e => setMinutes(parseInt(e.target.value)||1)} style={{ width:100 }} />
        </div>
        <div className="form-group">
          <label className="form-label">Reason (optional)</label>
          <input className="form-input" value={reason} onChange={e => setReason(e.target.value)} placeholder="Technical issue..." />
        </div>
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:16 }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className={`btn ${action==='add'?'btn-success':'btn-danger'}`} disabled={loading}
            onClick={async () => { setLoading(true); try { await onConfirm(attempt.id, action==='add'?minutes:-minutes, reason); onClose(); } finally { setLoading(false); } }}>
            {loading ? 'Applying...' : action==='add' ? `+ Add ${minutes} min` : `− Deduct ${minutes} min`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Org Exams Panel ───────────────────────────────────────────────────────────
function OrgExamsPanel({ org, onClose }) {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`/api/organizations/${org.id}/exams`).then(r => setExams(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [org.id]);
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:700 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 className="modal-title" style={{ margin:0 }}>📋 {org.name} — Exams ({exams.length})</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:22 }}>✕</button>
        </div>
        {loading ? <div style={{ textAlign:'center', padding:32 }}><div className="spinner" /></div> :
          exams.length === 0 ? <div style={{ textAlign:'center', padding:32, color:'var(--text-secondary)' }}>No exams yet</div> : (
          <div className="table-container" style={{ border:'none' }}>
            <table>
              <thead><tr><th>Exam</th><th>Instructor</th><th>Status</th><th>Enrolled</th><th>Active/Done</th><th></th></tr></thead>
              <tbody>
                {exams.map(ex => (
                  <tr key={ex.id}>
                    <td><div style={{ fontWeight:600, fontSize:13 }}>{ex.title}</div><div style={{ fontSize:11, color:'var(--text-muted)' }}>{ex.duration_minutes}min · {ex.total_marks}marks</div></td>
                    <td style={{ fontSize:13 }}>{ex.instructor_name||'—'}</td>
                    <td><span className={`badge badge-${ex.is_published?'green':'yellow'}`}>{ex.is_published?'Published':'Draft'}</span></td>
                    <td style={{ textAlign:'center', fontWeight:600 }}>{ex.enrolled_count||0}</td>
                    <td style={{ fontSize:12 }}><span style={{ color:'var(--accent-warning)' }}>{ex.active_count||0}</span> / <span style={{ color:'var(--accent-success)' }}>{ex.submitted_count||0}</span></td>
                    <td><button className="btn btn-secondary btn-sm" onClick={() => { onClose(); navigate(`/live/${ex.id}`); }}>📡 Live</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main AdminDashboard ──────────────────────────────────────────────────────
export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { confirm } = useConfirm();

  const handleDeleteOrg = async (org) => {
    const ok = await confirm({
      title: `Delete "${org.name}"?`,
      message: 'This permanently removes the organization and ALL its data.',
      impact: [
        'All exams and questions deleted',
        'All student attempts and answers deleted',
        'All enrollment records removed',
        'Proctor accounts unlinked (not deleted)',
        '⛔ Cannot be undone',
      ],
      confirmLabel: 'Delete Organization', variant: 'danger', requirePassword: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`/api/organizations/${org.id}`);
      toast.success(`"${org.name}" deleted`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to delete'); }
  };
  const isAdmin = user?.role === 'admin';
  const [activeTab,     setActiveTab]     = useState('overview');
  const [stats,         setStats]         = useState({});
  const [orgs,          setOrgs]          = useState([]);
  const [attempts,      setAttempts]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [filter,        setFilter]        = useState('all');
  const [orgSearch,     setOrgSearch]     = useState('');
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [showOrgImport, setShowOrgImport]  = useState(false);
  const [editOrg,       setEditOrg]       = useState(null);
  const [blockOrg,      setBlockOrg]      = useState(null);
  const [viewExamsOrg,  setViewExamsOrg]  = useState(null);
  const [resumeAttempt, setResumeAttempt] = useState(null);
  const [adjustAttempt, setAdjustAttempt] = useState(null);

  const handleImportOrgs = async (rows) => {
    const res = await axios.post('/api/organizations/import/bulk', { rows });
    fetchAll();
    return res.data;
  };

  const fetchAll = useCallback(() => {
    const requests = [
      axios.get('/api/attempts/stats/dashboard'),
      axios.get('/api/attempts/all'),
    ];
    // Only admin can fetch organizations
    if (isAdmin) requests.push(axios.get('/api/organizations'));

    Promise.all(requests).then(([stRes, atRes, orgRes]) => {
      setStats(stRes.data);
      setAttempts(atRes.data);
      if (orgRes) setOrgs(orgRes.data);
    }).catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleResume = async (attemptId, extra, useFullTime=false) => {
    const res = await axios.post(`/api/attempts/${attemptId}/resume`, { extra_minutes: extra, use_full_time: useFullTime });
    toast.success(`Resumed — ${Math.ceil(res.data.remaining_seconds/60)} min left`);
    fetchAll();
  };
  const handleAdjustTime = async (attemptId, minutes, reason) => {
    await axios.post(`/api/attempts/${attemptId}/adjust-time`, { minutes, reason });
    toast.success(`Time ${minutes>0?'added':'deducted'}: ${Math.abs(minutes)} min`);
    fetchAll();
  };
  const handleTerminate = async (a) => {
    const ok = await confirm({
      title: 'Terminate Exam?',
      message: `Terminate "${a.exam_title}" for ${a.student_name}?`,
      impact: ['Student will be immediately removed from the exam', 'Answers submitted so far will be saved', 'The attempt will be marked as Terminated', 'This action cannot be undone'],
      confirmLabel: 'Terminate Exam',
      variant: 'danger',
    });
    if (!ok) return;
    const reason = window.prompt('Reason for termination:', 'Admin terminated');
    if (reason === null) return;
    try { await axios.post(`/api/attempts/${a.id}/terminate`, { reason }); toast.success('Terminated'); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const filteredAttempts = filter === 'all' ? attempts : filter === 'terminated' ? attempts.filter(a => a.status==='terminated') : filter === 'in_progress' ? attempts.filter(a => a.status==='in_progress') : attempts.filter(a => (a.trust_score||100)<50);
  const filteredOrgs = orgSearch ? orgs.filter(o => o.name.toLowerCase().includes(orgSearch.toLowerCase())||o.slug.includes(orgSearch)) : orgs;
  const chartData = [
    { name:'Total',  value:stats.total_attempts||0, color:'var(--accent-primary)' },
    { name:'Passed', value:Math.round(((stats.pass_rate||0)/100)*(stats.total_attempts||0)), color:'var(--accent-success)' },
    { name:'Failed', value:(stats.total_attempts||0)-Math.round(((stats.pass_rate||0)/100)*(stats.total_attempts||0)), color:'var(--accent-danger)' },
  ];
  const statusBadge = s => {
    const m = { in_progress:'badge-blue', submitted:'badge-green', terminated:'badge-red', graded:'badge-green' };
    return <span className={`badge ${m[s]||'badge-yellow'}`}>{s?.replace('_',' ')}</span>;
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="layout">
      <Sidebar />
      <main className="main-content">
        <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h1 className="page-title">Admin Dashboard</h1>
            <p className="page-subtitle">{orgs.length} organizations · {stats.total_students||0} students total</p>
          </div>
          <div style={{ display:'flex', gap:8 }}>
              <button className="btn btn-secondary" onClick={() => setShowOrgImport(true)}>📥 Import Organizations</button>
              <button className="btn btn-primary" onClick={() => setShowCreateOrg(true)}>🏢 Add Organization</button>
            </div>
        </div>

        {/* Stats */}
        <div className="card-grid card-grid-4 mb-6">
          {[
            { val:orgs.length,              label:'Organizations', color:'var(--accent-secondary)' },
            { val:stats.total_students||0,  label:'Students',      color:'var(--accent-primary)'   },
            { val:stats.total_attempts||0,  label:'Attempts',      color:'var(--accent-warning)'   },
            { val:`${stats.pass_rate||0}%`, label:'Pass Rate',     color:'var(--accent-success)'   },
          ].map(s => <div key={s.label} className="stat-card"><div className="stat-value" style={{ color:s.color }}>{s.val}</div><div className="stat-label">{s.label}</div></div>)}
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', gap:8, marginBottom:20 }}>
          {[['overview','📊 Overview'],['organizations','🏢 Organizations'],['attempts','📋 Attempts']].map(([tab,label]) => (
            <button key={tab} className="btn btn-secondary" onClick={() => setActiveTab(tab)}
              style={activeTab===tab ? { borderColor:'var(--accent-primary)', color:'var(--accent-primary)', background:'rgba(79,142,247,0.1)' } : {}}>
              {label}
            </button>
          ))}
        </div>

        {/* ── OVERVIEW ──────────────────────────────────────────────────── */}
        {activeTab === 'overview' && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
            <div className="card">
              <h3 style={{ fontSize:15, fontWeight:600, marginBottom:20 }}>Attempt Overview</h3>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={chartData} margin={{ top:0, right:0, left:-20, bottom:0 }}>
                  <XAxis dataKey="name" tick={{ fill:'var(--text-secondary)', fontSize:12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill:'var(--text-secondary)', fontSize:12 }} axisLine={false} tickLine={false} />
                  <ChartTooltip contentStyle={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:8 }} />
                  <Bar dataKey="value" radius={[6,6,0,0]}>{chartData.map((e,i) => <Cell key={i} fill={e.color} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="card">
              <h3 style={{ fontSize:15, fontWeight:600, marginBottom:16 }}>Quick Actions</h3>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                <button className="btn btn-primary" onClick={() => setShowCreateOrg(true)}>🏢 Add Organization</button>
                <button className="btn btn-secondary" onClick={() => setActiveTab('organizations')}>📋 Manage Organizations</button>
                <button className="btn btn-secondary" onClick={() => setActiveTab('attempts')}>⚠ Review Attempts ({attempts.filter(a=>a.status==='terminated').length} terminated)</button>
              </div>
              {orgs.filter(o=>!o.is_active).length > 0 && (
                <div className="alert alert-danger" style={{ margin:'12px 0 0', fontSize:13 }}>
                  ⚠ {orgs.filter(o=>!o.is_active).length} org(s) are blocked
                </div>
              )}
            </div>
            <div className="card" style={{ gridColumn:'1/-1' }}>
              <h3 style={{ fontSize:15, fontWeight:600, marginBottom:16 }}>Organizations at a Glance</h3>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(230px,1fr))', gap:12 }}>
                {orgs.map(org => (
                  <div key={org.id} style={{ padding:'14px 16px', background:'var(--bg-elevated)', borderRadius:'var(--radius-sm)', border:`1px solid ${org.is_active?'var(--border)':'rgba(239,68,68,0.3)'}` }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                      <div style={{ fontWeight:600, fontSize:13, flex:1, marginRight:8 }}>{org.name}</div>
                      <span className={`badge badge-${org.is_active?'green':'red'}`} style={{ fontSize:10, flexShrink:0 }}>{org.is_active?'Active':'Blocked'}</span>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:4, fontSize:12, textAlign:'center', marginBottom:10 }}>
                      <div><div style={{ fontWeight:700, fontSize:15, color:'var(--accent-primary)' }}>{org.student_count||0}</div><div style={{ color:'var(--text-muted)' }}>students</div></div>
                      <div><div style={{ fontWeight:700, fontSize:15, color:'var(--accent-warning)' }}>{org.total_exams||0}</div><div style={{ color:'var(--text-muted)' }}>exams</div></div>
                      <div><div style={{ fontWeight:700, fontSize:15, color:'var(--accent-success)' }}>{org.total_attempts||0}</div><div style={{ color:'var(--text-muted)' }}>attempts</div></div>
                    </div>
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                      <button className="btn btn-secondary btn-sm" style={{ flex:1, justifyContent:'center', fontSize:11 }} onClick={() => setViewExamsOrg(org)}>Exams</button>
                      <button className="btn btn-secondary btn-sm" style={{ flex:1, justifyContent:'center', fontSize:11 }} onClick={() => setEditOrg(org)}>Edit</button>
                      <button className={`btn btn-sm ${org.is_active?'btn-danger':'btn-success'}`} style={{ flex:1, justifyContent:'center', fontSize:11 }} onClick={() => setBlockOrg(org)}>
                        {org.is_active?'Block':'Unblock'}
                      </button>
                      <button className="btn btn-danger btn-sm" style={{ fontSize:11 }} onClick={() => handleDeleteOrg(org)}>🗑</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── ORGANIZATIONS ──────────────────────────────────────────────── */}
        {activeTab === 'organizations' && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
              <input className="form-input" style={{ width:260, padding:'7px 12px' }} placeholder="🔍 Search organizations..." value={orgSearch} onChange={e => setOrgSearch(e.target.value)} />
              <button className="btn btn-primary" onClick={() => setShowCreateOrg(true)}>🏢 Add Organization</button>
            </div>
            <div className="card" style={{ padding:0 }}>
              <div className="table-container" style={{ border:'none' }}>
                <table>
                  <thead><tr><th>Organization</th><th style={{ textAlign:'center' }}>Students</th><th style={{ textAlign:'center' }}>Instructors</th><th>Exams</th><th>Status</th><th>Actions</th></tr></thead>
                  <tbody>
                    {filteredOrgs.length === 0 && <tr><td colSpan={6} style={{ textAlign:'center', padding:32, color:'var(--text-secondary)' }}>No organizations found</td></tr>}
                    {filteredOrgs.map(org => (
                      <tr key={org.id}>
                        <td>
                          <div style={{ fontWeight:600 }}>{org.name}</div>
                          <div style={{ fontSize:12, fontFamily:'var(--font-mono)', color:'var(--text-muted)' }}>{org.slug}</div>
                          {org.contact_email && <div style={{ fontSize:12, color:'var(--text-muted)' }}>{org.contact_email}</div>}
                        </td>
                        <td style={{ textAlign:'center' }}>
                          <div style={{ fontSize:18, fontWeight:700, color:'var(--accent-primary)' }}>{org.student_count||0}</div>
                        </td>
                        <td style={{ textAlign:'center' }}>
                          <div style={{ fontSize:18, fontWeight:700, color:'var(--accent-secondary)' }}>{org.instructor_count||0}</div>
                        </td>
                        <td>
                          <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                            {parseInt(org.running_exams||0) > 0 && <span className="badge badge-green">▶ {org.running_exams} running</span>}
                            {parseInt(org.completed_exams||0) > 0 && <span className="badge badge-blue">✓ {org.completed_exams} done</span>}
                            {parseInt(org.planned_exams||0) > 0 && <span className="badge badge-yellow">◷ {org.planned_exams} planned</span>}
                            {!parseInt(org.total_exams||0) && <span style={{ fontSize:12, color:'var(--text-muted)' }}>No exams</span>}
                          </div>
                          <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:3 }}>{org.total_enrollments||0} enrolled · {org.total_attempts||0} attempts</div>
                        </td>
                        <td><span className={`badge badge-${org.is_active?'green':'red'}`}>{org.is_active?'Active':'Blocked'}</span></td>
                        <td>
                          <div style={{ display:'flex', gap:6 }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => setViewExamsOrg(org)}>Exams</button>
                            <button className="btn btn-secondary btn-sm" onClick={() => setEditOrg(org)}>Edit</button>
                            <button className={`btn btn-sm ${org.is_active?'btn-danger':'btn-success'}`} onClick={() => setBlockOrg(org)}>{org.is_active?'Block':'Unblock'}</button>
                            <button className="btn btn-danger btn-sm" onClick={() => handleDeleteOrg(org)}>🗑 Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ── ATTEMPTS ─────────────────────────────────────────────────── */}
        {activeTab === 'attempts' && (
          <div>
            <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
              {[['all','All'],['in_progress','In Progress'],['terminated','Terminated'],['low_trust','Low Trust']].map(([val,label]) => (
                <button key={val} className="btn btn-secondary btn-sm" onClick={() => setFilter(val)}
                  style={filter===val ? { borderColor:'var(--accent-primary)', color:'var(--accent-primary)', background:'rgba(79,142,247,0.1)' } : {}}>
                  {label}
                </button>
              ))}
            </div>
            <div className="card" style={{ padding:0 }}>
              <div className="table-container" style={{ border:'none' }}>
                <table>
                  <thead><tr><th>Student</th><th>Exam</th><th>Status</th><th>Trust</th><th>Time Left</th><th>IP</th><th>Actions</th></tr></thead>
                  <tbody>
                    {filteredAttempts.length === 0 && <tr><td colSpan={7} style={{ textAlign:'center', padding:32, color:'var(--text-secondary)' }}>No attempts</td></tr>}
                    {filteredAttempts.map(a => (
                      <tr key={a.id}>
                        <td style={{ fontWeight:500 }}>{a.student_name}</td>
                        <td style={{ fontSize:13, color:'var(--text-secondary)' }}>{a.exam_title}</td>
                        <td>{statusBadge(a.status)}</td>
                        <td><span style={{ fontFamily:'var(--font-mono)', fontSize:13, color:(a.trust_score||100)>=75?'var(--accent-success)':(a.trust_score||100)>=40?'var(--accent-warning)':'var(--accent-danger)' }}>{Math.round(a.trust_score||100)}%</span></td>
                        <td style={{ fontFamily:'var(--font-mono)', fontSize:12 }}>{a.remaining_seconds ? `${Math.ceil(a.remaining_seconds/60)}m` : '—'}</td>
                        <td style={{ fontFamily:'var(--font-mono)', fontSize:12, color:'var(--text-secondary)' }}>{a.ip_address||'—'}</td>
                        <td>
                          <div style={{ display:'flex', gap:5 }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/proctor/${a.id}`)}>Report</button>
                            <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/live/${a.exam_id}`)}>Live</button>
                            {a.status==='terminated' && <button className="btn btn-success btn-sm" onClick={() => setResumeAttempt(a)}>▶</button>}
                            {a.status==='in_progress' && <>
                              <button className="btn btn-secondary btn-sm" style={{ color:'var(--accent-warning)' }} onClick={() => setAdjustAttempt(a)}>⏱</button>
                              <button className="btn btn-danger btn-sm" onClick={() => handleTerminate(a)}>⛔</button>
                            </>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </main>

      {showOrgImport && (
        <ImportModal
          title="Import Organizations"
          templateFilename="organizations_template.csv"
          columns={[
            { key:'name',           label:'Name',           required:true,  example:'Tech Institute of Excellence', example2:'Global Learning Hub', aliases:['organization','Organization'] },
            { key:'slug',           label:'Slug',           required:false, example:'tech-institute',               example2:'global-learning' },
            { key:'description',    label:'Description',    required:false, example:'Premier tech training institute', example2:'Online learning platform' },
            { key:'contact_email',  label:'Contact Email',  required:false, example:'admin@techins.com',            example2:'info@globallearn.com', aliases:['email','Email'] },
            { key:'admin_name',     label:'Admin Name',     required:false, example:'John Smith',                   example2:'Jane Doe',  aliases:['admin_name','Admin Name'] },
            { key:'admin_email',    label:'Admin Email',    required:false, example:'john@techins.com',             example2:'jane@globallearn.com', aliases:['admin_email','Admin Email'] },
            { key:'admin_password', label:'Admin Password', required:false, example:'Org@123',                      example2:'Org@123', aliases:['admin_password','Admin Password'] },
          ]}
          onImport={handleImportOrgs}
          onClose={() => setShowOrgImport(false)}
        />
      )}
      {showCreateOrg && <CreateOrgModal onClose={() => setShowCreateOrg(false)} onCreated={fetchAll} />}
      {editOrg       && <EditOrgModal  org={editOrg}  onClose={() => setEditOrg(null)}  onSaved={fetchAll} />}
      {blockOrg      && <BlockOrgModal org={blockOrg} onClose={() => setBlockOrg(null)} onDone={fetchAll} />}
      {viewExamsOrg  && <OrgExamsPanel org={viewExamsOrg} onClose={() => setViewExamsOrg(null)} />}
      {resumeAttempt && <ResumeModal   attempt={resumeAttempt} onClose={() => setResumeAttempt(null)} onConfirm={handleResume} />}
      {adjustAttempt && <TimeAdjustModal attempt={adjustAttempt} onClose={() => setAdjustAttempt(null)} onConfirm={handleAdjustTime} />}
    </div>
  );
}
